/* Choosing people by what we know about them.
 *
 * "Women aged 20–30", "everybody baptised", "the over-60s in the choir".
 * One builder, used in three places that all mean the same thing by it: the
 * Members table, the Meet / Go live window, and the message composer. The
 * server checks every rule again (groups/audience_filter.py); this only
 * offers what that file accepts.
 *
 * A RULE IS A SENTENCE. Field, comparison, value — "Age · between · 20 and
 * 30" — so it reads the way somebody would say it. Rules join with all or
 * any; one level only, because the person building this is a church
 * secretary, not writing a query.
 *
 * HALF-WRITTEN RULES ARE LEFT OUT, not sent. Somebody choosing "between"
 * has not typed the numbers yet, and the count under the rules should keep
 * showing the last complete answer rather than an error.
 */
import { useMemo } from 'react'
import { AlertCircle, Loader2, Plus, Users, X } from 'lucide-react'
import { useAudienceFields, useAudiencePreview } from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import type {
  AudienceField,
  AudienceFilter,
  AudienceOp,
  AudienceRule,
} from '@/types/api'

export const EMPTY_FILTER: AudienceFilter = { match: 'all', rules: [] }

const MAX_RULES = 10

/** What each comparison is called, by the kind of field it is on. */
function opLabel(field: AudienceField, op: AudienceOp): string {
  const numeric = field.type === 'number'
  switch (op) {
    case 'is':
      return 'is'
    case 'contains':
      return 'contains'
    case 'under':
      return field.type === 'age' ? 'younger than' : numeric ? 'less than' : 'under'
    case 'over':
      return field.type === 'age' ? 'older than' : numeric ? 'more than' : 'over'
    case 'between':
      return 'between'
    case 'before':
      return 'before'
    case 'after':
      return 'after'
    case 'ticked':
      return 'is ticked'
    case 'not_ticked':
      return 'is not ticked'
    case 'any_of':
      return 'is any of'
    case 'answered':
      return 'is answered'
    case 'not_answered':
      return 'is not answered'
    case 'absent_of_last':
      return 'absent'
    case 'absent_from':
      return 'absent from'
  }
}

/** Comparisons that need nothing more than the field and the operator. */
const NO_VALUE: AudienceOp[] = ['ticked', 'not_ticked', 'answered', 'not_answered']

function blank(v: unknown): boolean {
  return v === undefined || v === null || String(v).trim() === ''
}

/** Whether a rule says enough to be asked. */
export function isComplete(rule: AudienceRule): boolean {
  if (!rule.field || !rule.op) return false
  if (NO_VALUE.includes(rule.op)) return true
  if (rule.op === 'between') return !blank(rule.min) && !blank(rule.max)
  if (rule.op === 'any_of') return (rule.values ?? []).length > 0
  if (rule.op === 'absent_of_last') return !blank(rule.value) && !blank(rule.of)
  return !blank(rule.value)
}

/** The filter as it should be sent: complete rules only, or null when
 *  there are none yet. */
export function completeFilter(filter: AudienceFilter): AudienceFilter | null {
  const rules = filter.rules.filter(isComplete).map((r) => {
    const out: AudienceRule = { field: r.field, op: r.op }
    if (r.value !== undefined && r.value !== null) out.value = r.value
    if (r.values) out.values = r.values
    if (!blank(r.min)) out.min = r.min
    if (!blank(r.max)) out.max = r.max
    if (r.op === 'absent_of_last') {
      out.value = Number(r.value)
      out.of = Number(r.of)
    }
    return out
  })
  return rules.length ? { match: filter.match, rules } : null
}

function freshRule(field: AudienceField, op: AudienceOp = field.ops[0]): AudienceRule {
  if (field.type === 'attendance') {
    return op === 'absent_of_last'
      ? { field: field.key, op, value: 2, of: 4 }
      : { field: field.key, op, value: field.choices?.[0]?.value ?? null }
  }
  return {
    field: field.key,
    op,
    value: field.type === 'gender' ? 'female' : null,
  }
}

const control =
  'h-10 rounded border border-line-2 bg-surface px-s2 text-base outline-none ' +
  'focus:shadow-focus disabled:opacity-60'

/* ---------------------------------------------------------------- builder */

export function RuleBuilder({
  churchId,
  value,
  onChange,
  compact = false,
}: {
  churchId: string | null
  value: AudienceFilter
  onChange: (next: AudienceFilter) => void
  /** Inside a dialog: tighter, and the match line folds into one row. */
  compact?: boolean
}) {
  const fieldsQ = useAudienceFields(churchId)
  const fields = fieldsQ.data ?? []
  const byKey = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.key, f])),
    [fields],
  )

  function setRule(i: number, rule: AudienceRule) {
    onChange({ ...value, rules: value.rules.map((r, j) => (j === i ? rule : r)) })
  }
  function removeRule(i: number) {
    onChange({ ...value, rules: value.rules.filter((_, j) => j !== i) })
  }
  function addRule() {
    const first = fields[0]
    if (!first) return
    onChange({ ...value, rules: [...value.rules, freshRule(first)] })
  }

  if (fieldsQ.isLoading) {
    return (
      <div className="flex justify-center py-s4">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </div>
    )
  }

  return (
    <div className={cn('space-y-s3', compact && 'space-y-s2')}>
      {value.rules.length > 1 ? (
        <div className="flex flex-wrap items-center gap-s2 text-sm text-ink-2">
          People who match
          <div role="radiogroup" className="inline-flex rounded-pill border border-line-2 p-0.5">
            {(['all', 'any'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={value.match === m}
                onClick={() => onChange({ ...value, match: m })}
                className={cn(
                  'rounded-pill px-s3 py-1 text-sm font-semibold transition-colors',
                  value.match === m ? 'bg-brand text-ink-on-brand' : 'text-ink-2 hover:bg-hover',
                )}
              >
                {m === 'all' ? 'all' : 'any'}
              </button>
            ))}
          </div>
          of these
        </div>
      ) : null}

      {value.rules.map((rule, i) => {
        const field = byKey[rule.field]
        return (
          <div key={i}>
            {i > 0 ? (
              <div className="mb-s2 text-xs font-semibold uppercase tracking-wide text-ink-3">
                {value.match === 'all' ? 'and' : 'or'}
              </div>
            ) : null}
            <RuleRow
              fields={fields}
              field={field}
              rule={rule}
              onChange={(r) => setRule(i, r)}
              onRemove={() => removeRule(i)}
            />
          </div>
        )
      })}

      {value.rules.length < MAX_RULES ? (
        <button
          type="button"
          onClick={addRule}
          disabled={!fields.length}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline disabled:text-ink-3"
        >
          <Plus className="h-4 w-4" aria-hidden />
          {value.rules.length ? 'Add another rule' : 'Add a rule'}
        </button>
      ) : null}
    </div>
  )
}

function RuleRow({
  fields,
  field,
  rule,
  onChange,
  onRemove,
}: {
  fields: AudienceField[]
  field: AudienceField | undefined
  rule: AudienceRule
  onChange: (rule: AudienceRule) => void
  onRemove: () => void
}) {
  return (
    <div className="flex flex-wrap items-start gap-s2 rounded border border-line-2 bg-surface-2 p-s2">
      <select
        aria-label="Field"
        value={rule.field}
        onChange={(e) => {
          const next = fields.find((f) => f.key === e.target.value)
          if (next) onChange(freshRule(next))
        }}
        className={cn(control, 'min-w-[140px] flex-1 sm:flex-none')}
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>

      {field ? (
        <>
          <select
            aria-label="Comparison"
            value={rule.op}
            onChange={(e) =>
              onChange(
                // Attendance comparisons take different values entirely,
                // so each starts from its own defaults.
                field.type === 'attendance'
                  ? freshRule(field, e.target.value as AudienceOp)
                  : {
                      field: rule.field,
                      op: e.target.value as AudienceOp,
                      // Keep a typed value across under/over; drop it where it
                      // would mean something else.
                      value: e.target.value === 'between' ? null : rule.value,
                    },
              )
            }
            className={cn(control, 'min-w-[120px]')}
          >
            {field.ops.map((op) => (
              <option key={op} value={op}>
                {opLabel(field, op)}
              </option>
            ))}
          </select>
          <RuleValue field={field} rule={rule} onChange={onChange} />
        </>
      ) : null}

      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove this rule"
        className="ml-auto grid h-10 w-10 shrink-0 place-items-center rounded text-ink-2 hover:bg-hover"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

function RuleValue({
  field,
  rule,
  onChange,
}: {
  field: AudienceField
  rule: AudienceRule
  onChange: (rule: AudienceRule) => void
}) {
  if (NO_VALUE.includes(rule.op)) return null

  if (field.type === 'attendance') {
    if (rule.op === 'absent_from') {
      return (
        <select
          aria-label="Service day"
          value={String(rule.value ?? '')}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          className={cn(control, 'min-w-[180px] flex-1')}
        >
          {(field.choices ?? []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      )
    }
    return (
      <div className="flex flex-wrap items-center gap-s2">
        <input
          aria-label="Times absent"
          type="number"
          min={1}
          max={52}
          value={String(rule.value ?? '')}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          className={cn(control, 'w-[72px]')}
        />
        <span className="text-sm text-ink-2">of the last</span>
        <input
          aria-label="Service days"
          type="number"
          min={1}
          max={52}
          value={String(rule.of ?? '')}
          onChange={(e) => onChange({ ...rule, of: e.target.value })}
          className={cn(control, 'w-[72px]')}
        />
        <span className="text-sm text-ink-2">service days</span>
      </div>
    )
  }

  if (field.type === 'gender') {
    return (
      <select
        aria-label="Gender"
        value={String(rule.value ?? 'female')}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
        className={cn(control, 'min-w-[110px]')}
      >
        <option value="female">Female</option>
        <option value="male">Male</option>
      </select>
    )
  }

  if (rule.op === 'any_of') {
    const chosen = new Set(rule.values ?? [])
    return (
      <div className="flex min-h-10 flex-1 flex-wrap items-center gap-1.5">
        {(field.options ?? []).map((o) => {
          const on = chosen.has(o)
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              onClick={() => {
                const next = new Set(chosen)
                if (on) next.delete(o)
                else next.add(o)
                onChange({ ...rule, values: [...next] })
              }}
              className={cn(
                'rounded-pill border px-s3 py-1 text-sm font-semibold transition-colors',
                on
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-line-2 bg-surface text-ink-2 hover:bg-hover',
              )}
            >
              {o}
            </button>
          )
        })}
      </div>
    )
  }

  const type =
    field.type === 'date_time' ? 'date' : field.type === 'text_box' ? 'text' : 'number'
  const width = type === 'text' ? 'min-w-[160px] flex-1' : 'w-[120px]'
  const unit = field.type === 'age' ? 'years' : null

  if (rule.op === 'between') {
    return (
      <div className="flex flex-wrap items-center gap-s2">
        <input
          aria-label="From"
          type={type}
          min={type === 'number' && field.type === 'age' ? 0 : undefined}
          value={String(rule.min ?? '')}
          onChange={(e) => onChange({ ...rule, min: e.target.value })}
          className={cn(control, width)}
        />
        <span className="text-sm text-ink-2">and</span>
        <input
          aria-label="To"
          type={type}
          min={type === 'number' && field.type === 'age' ? 0 : undefined}
          value={String(rule.max ?? '')}
          onChange={(e) => onChange({ ...rule, max: e.target.value })}
          className={cn(control, width)}
        />
        {unit ? <span className="text-sm text-ink-2">{unit}</span> : null}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-s2">
      <input
        aria-label="Value"
        type={type}
        min={type === 'number' && field.type === 'age' ? 0 : undefined}
        value={String(rule.value ?? '')}
        maxLength={type === 'text' ? 100 : undefined}
        placeholder={type === 'text' ? 'Type an answer' : undefined}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
        className={cn(control, width)}
      />
      {unit ? <span className="text-sm text-ink-2">{unit}</span> : null}
    </div>
  )
}

/* ---------------------------------------------------------------- the count */

/** "142 people match" under the rules, with a few faces and any warning.
 *
 *  Asks the server — the same resolve a send uses — once the rules stop
 *  changing for a moment. */
export function AudienceCount({
  churchId,
  filter,
  groupId,
}: {
  churchId: string | null
  filter: AudienceFilter
  groupId?: string | null
}) {
  // Debounced as text: a fresh object every render would restart the timer
  // forever.
  const settled = useDebounced(JSON.stringify(completeFilter(filter)), 400)
  const complete = useMemo(
    () => JSON.parse(settled) as AudienceFilter | null,
    [settled],
  )
  const preview = useAudiencePreview(
    churchId,
    { audience_kind: 'filter', group_id: groupId || null, audience_filter: complete },
    complete !== null,
  )

  if (complete === null) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-ink-2">
        <Users className="h-4 w-4" aria-hidden />
        Add a rule to choose who this is for.
      </p>
    )
  }

  if (preview.isError) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-danger" role="alert">
        <AlertCircle className="h-4 w-4" aria-hidden />
        {(preview.error as Error).message}
      </p>
    )
  }

  const p = preview.data
  if (!p) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-ink-2">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Counting…
      </p>
    )
  }

  const others = p.count - p.sample.length
  return (
    <div
      className={cn('rounded border border-line-2 bg-surface px-s3 py-s2', preview.isFetching && 'opacity-70')}
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center gap-s2">
        {p.sample.length ? (
          <div className="flex -space-x-2">
            {p.sample.map((s) => (
              <Avatar
                key={s.id}
                name={s.name}
                src={s.avatar_url}
                size="sm"
                className="ring-2 ring-surface"
              />
            ))}
          </div>
        ) : null}
        <span className="text-base">
          <strong className="font-extrabold">{p.count}</strong>{' '}
          {p.count === 1 ? 'person matches' : 'people match'}
        </span>
      </div>
      {p.sample.length ? (
        <p className="mt-1 text-sm text-ink-2">
          {p.sample.map((s) => s.name).join(', ')}
          {others > 0 ? ` and ${others} more` : ''}
        </p>
      ) : (
        <p className="mt-1 text-sm text-ink-2">Nobody matches these rules yet.</p>
      )}
      {p.without_birthday ? (
        <p className="mt-1 text-sm text-ink-2">
          {p.without_birthday}{' '}
          {p.without_birthday === 1 ? 'person has' : 'people have'} no birthday on
          their profile, so an age rule cannot include them.
        </p>
      ) : null}
    </div>
  )
}
